import { sharedShowApi } from '@/api/sharedShowApi'
import { demoWorkspaceApi } from '@/api/demoWorkspaceApi'
import { addDays } from '@/features/scheduler/schedulerDates'
import { api } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  BookAppointmentInput,
  CreateSaleInput,
} from '@/features/actions/schemas/actionSchemas'
import type { ActionSubject } from '@/features/actions/types'
import { customersApi } from '@/api/customersApi'
import { demoScheduler } from '@/features/scheduler/demo/demoScheduler'
import { toOffsetDateTime, toZonedParts } from '@/features/scheduler/schedulerDates'
import type { Appointment, CalendarBoard } from '@/features/scheduler/types'
import type { Credits, PipelineState } from '@/types/models'
import { env } from '@/lib/env'

interface CalendarResponse {
  from: string
  to: string
  timezone: string
  slotMinutes: number
  startStepMinutes: number
  allowedDurationMinutes: number[]
  openingHours: { start: string; end: string }
  devices: { id: number; name: string; position: number }[]
  appointments: {
    id: number
    deviceId: number
    customerPublicId: string
    customerName: string
    startsAt: string
    endsAt: string
    kind: string
    kindLabel: string | null
    status: string
    statusLabel: string | null
    creditState: string | null
    creditStateLabel: string | null
  }[]
}

interface AppointmentCreatedResponse {
  appointment: AppointmentResponse
  credits: Credits | null
  /** Whether this booking moved the customer through the CRM funnel. */
  stageChanged: boolean
  previousStage: string | null
  pipeline: PipelineState | null
}

interface CheckInResponse {
  compliance?: { treatmentId: string }
  appointment: {
    id: number
    status: string
    statusLabel: string | null
    checkedInAt: string | null
    checkedInByUserName: string | null
    creditState: string | null
    creditStateLabel: string | null
  }
  credits: Credits | null
}

export interface AppointmentCreated {
  appointment: Appointment
  credits: Credits | null
  /** True when this booking advanced the customer through the funnel. */
  stageChanged: boolean
  /** The stage code they came from; the label is only sent for the new one. */
  previousStage: string | null
  pipeline: PipelineState | null
}

export interface CheckedIn {
  compliance?: { treatmentId: string }
  appointmentId: string
  status: string
  statusLabel: string | null
  checkedInAt: string | null
  checkedInBy: string | null
  creditState: string | null
  creditStateLabel: string | null
  credits: Credits | null
}

type AppointmentResponse = CalendarResponse['appointments'][number] & { deviceName?: string }

/**
 * `deviceName` is on the booking response but **not** on a calendar card — the
 * published schema shares one `Appointment` type for both and so overstates it.
 * Resolving it here, against the devices the same response carried, means every
 * consumer can just read `appointment.deviceName`.
 */
function toAppointment(
  wire: AppointmentResponse,
  timezone: string,
  deviceNames?: Map<string, string>,
): Appointment {
  const starts = toZonedParts(wire.startsAt, timezone)
  const ends = toZonedParts(wire.endsAt, timezone)

  return {
    id: String(wire.id),
    deviceId: String(wire.deviceId),
    deviceName: wire.deviceName ?? deviceNames?.get(String(wire.deviceId)) ?? '—',
    customerId: wire.customerPublicId,
    customerName: wire.customerName,
    date: starts.date,
    start: starts.time,
    end: ends.time,
    kind: wire.kind,
    kindLabel: wire.kindLabel,
    status: wire.status,
    statusLabel: wire.statusLabel,
    creditState: wire.creditState,
    creditStateLabel: wire.creditStateLabel,
  }
}

function toBoard(response: CalendarResponse): CalendarBoard {
  const { timezone } = response
  const deviceNames = new Map(response.devices.map((d) => [String(d.id), d.name]))

  return {
    from: response.from,
    to: response.to,
    timezone,
    slotMinutes: response.slotMinutes,
    startStepMinutes: response.startStepMinutes,
    allowedDurationMinutes: response.allowedDurationMinutes,
    openingHours: response.openingHours,
    // Ids are numbers on the wire and strings everywhere above it.
    devices: [...response.devices]
      .sort((a, b) => a.position - b.position)
      .map((device) => ({ id: String(device.id), name: device.name, position: device.position })),
    appointments: response.appointments.map((wire) => toAppointment(wire, timezone, deviceNames)),
  }
}

/**
 * Everyone the booking form may pick from.
 *
 * This used to be derived from the calendar's ±90-day window, which meant the
 * only customers you could book were the ones who already had an appointment —
 * a new customer was unbookable until someone had booked them. `GET /api/customers`
 * removes the circularity, and the picker now offers the same people the Kunden
 * page lists, in the same order.
 */
async function bookableContacts(): Promise<ActionSubject[]> {
  // The server caps a page at 200; a studio with more customers needs a
  // searching picker, which this MVP does not have yet.
  const { items } = await customersApi.fetchPage({ size: 200 })

  return items.map((customer) => ({
    id: customer.id,
    name: customer.name,
    email: customer.email,
    phone: customer.phone,
  }))
}

export const schedulerApi = {
  fetchCalendar: async (from: string, to: string): Promise<CalendarBoard> => {
    if (env.complianceDevelopment) {
      const data = await (env.sharedShow ? sharedShowApi : demoWorkspaceApi).appointments()
      return {
        from: toOffsetDateTime(from, '00:00', data.timezone),
        to: toOffsetDateTime(addDays(to, 1), '00:00', data.timezone),
        timezone: data.timezone,
        slotMinutes: 15,
        startStepMinutes: 15,
        allowedDurationMinutes: [60],
        openingHours: { start: '08:00', end: '20:00' },
        devices: data.devices,
        appointments: data.appointments.filter((a) => a.date >= from && a.date <= to),
      }
    }
    if (env.demoMode) return demoScheduler.fetchCalendar(from, to)

    const response = await api.get<CalendarResponse>(endpoints.scheduler.calendar, {
      params: { from, to },
    })
    return toBoard(response)
  },

  fetchContacts: () =>
    env.demoMode && !env.complianceDevelopment ? demoScheduler.fetchContacts() : bookableContacts(),

  create: async (input: BookAppointmentInput, timezone: string): Promise<AppointmentCreated> => {
    if (env.sharedShow) return sharedShowApi.book(input)
    if (env.complianceDevelopment)
      return demoWorkspaceApi.create(input, (await customersApi.fetchDetail(input.subjectId)).name)
    if (env.demoMode) return demoScheduler.create(input)

    const payload = {
      customerPublicId: input.subjectId,
      deviceId: Number(input.deviceId),
      startsAt: toOffsetDateTime(input.date, input.start, timezone),
      durationMinutes: input.durationMinutes,
      kind: input.kind,
    }

    const created = await api.post<AppointmentCreatedResponse>(
      endpoints.scheduler.appointments,
      payload,
    )

    return {
      appointment: toAppointment(created.appointment, timezone),
      credits: created.credits ?? null,
      stageChanged: created.stageChanged ?? false,
      previousStage: created.previousStage ?? null,
      pipeline: created.pipeline ?? null,
    }
  },

  createSale: (input: CreateSaleInput) =>
    // NOT ON THE BACKEND YET — there is no sales endpoint.
    demoScheduler.createSale(input),

  checkIn: async (
    appointmentId: string,
    {
      widerrufConsent = false,
      signal,
      medicalClearance,
    }: {
      widerrufConsent?: boolean
      signal?: AbortSignal
      /** CMP-027 frontend proposal; backend must verify and atomically record this evidence. */
      medicalClearance?: {
        token: string
        requestId: string
        locationId: string
        customerId: string
        recordRevision: number
        workspaceRevision: number
      }
    } = {},
  ): Promise<CheckedIn> => {
    if (env.complianceDevelopment)
      throw new Error('Bitte öffnen Sie Behandlung vorbereiten und prüfen Sie die Voraussetzungen.')
    if (env.demoMode) return demoScheduler.checkIn(appointmentId, widerrufConsent)

    const result = await api.post<CheckInResponse>(
      endpoints.scheduler.appointmentCheckIn(appointmentId),
      medicalClearance
        ? { medicalClearance, ...(widerrufConsent ? { widerrufConsent: true } : {}) }
        : widerrufConsent
          ? { widerrufConsent: true }
          : undefined,
      { signal },
    )

    return {
      ...(result.compliance ? { compliance: result.compliance } : {}),
      appointmentId: String(result.appointment.id),
      status: result.appointment.status,
      statusLabel: result.appointment.statusLabel,
      checkedInAt: result.appointment.checkedInAt,
      checkedInBy: result.appointment.checkedInByUserName,
      creditState: result.appointment.creditState,
      creditStateLabel: result.appointment.creditStateLabel,
      credits: result.credits ?? null,
    }
  },
}
